// ── Fechas relativas ──────────────────────────────────────────────────────────

const today = () => new Date();
const isoDate = (d) => d.toISOString().split('T')[0];

export function resolveQuickDate(opcionId) {
  const t = today();
  switch (opcionId) {
    case 'today':      return isoDate(t);
    case 'yesterday':  return isoDate(new Date(t - 86400000));
    case 'this_week':  return isoDate(new Date(t - 4 * 86400000));
    case 'this_year':  return `${t.getFullYear()}-01-01`;
    case 'last_year':  return `${t.getFullYear() - 1}-01-01`;
    case 'older':      return `${t.getFullYear() - 2}-06-01`;
    case 'never':      return 'Nunca';
    default:           return isoDate(t);
  }
}

// ── Visibilidad de preguntas ──────────────────────────────────────────────────

export function getVisibleQuestions(preguntas, respuestas) {
  if (!preguntas) return [];
  return preguntas
    .filter(p => p.activo !== false)
    .filter(p => {
      if (!p.mostrarSi) return true;
      const { preguntaId, valorId, valorIds } = p.mostrarSi;
      const resp = respuestas[preguntaId];
      if (!resp) return false;
      // Accept single valorId or array valorIds
      const valid = valorIds || (valorId ? [valorId] : []);
      if (valid.length === 0) return true;
      if (resp.selectedId && valid.includes(resp.selectedId)) return true;
      if (Array.isArray(resp.selectedIds)) return resp.selectedIds.some(id => valid.includes(id));
      return false;
    })
    .sort((a, b) => a.orden - b.orden);
}

// ── Comprobación de respuesta completa ────────────────────────────────────────

export function isQuestionAnswered(pregunta, respuestas) {
  if (!pregunta.obligatorio) return true;
  const resp = respuestas[pregunta.id];
  if (!resp) return false;
  switch (pregunta.tipo) {
    case 'chips':
      if (pregunta.multiple) {
        const hasChips = Array.isArray(resp.selectedIds) && resp.selectedIds.length > 0;
        if (!hasChips) return false;
        // Si "otro" está seleccionado, requiere texto
        const hasOtro = resp.selectedIds.includes('otro');
        if (hasOtro && !resp.textoLibre?.trim()) return false;
        return true;
      }
      return !!resp.selectedId;
    case 'quick-date':
      if (resp.opcionId === 'custom') return !!(resp.date);
      return !!resp.opcionId;
    case 'opciones':
      return !!resp.selectedId;
    case 'texto':
      return !!(resp.text?.trim());
    default:
      return !!resp;
  }
}

export function areAllRequiredAnswered(preguntas, respuestas) {
  const visible = getVisibleQuestions(preguntas, respuestas);
  return visible.filter(p => p.obligatorio).every(p => isQuestionAnswered(p, respuestas));
}

// ── Construcción de actualizaciones para formData ────────────────────────────

export function buildFormDataUpdates(preguntas, respuestas) {
  if (!preguntas) return {};
  const updates = {};

  preguntas.forEach(pregunta => {
    const resp = respuestas[pregunta.id];
    if (!resp) return;

    switch (pregunta.tipo) {
      case 'chips': {
        if (pregunta.multiple) {
          if (!Array.isArray(resp.selectedIds) || resp.selectedIds.length === 0) break;
          if (pregunta.generarDescripcion) {
            const parts = resp.selectedIds
              .filter(id => id !== 'otro')
              .map(id => pregunta.opciones?.find(o => o.id === id)?.texto)
              .filter(Boolean);
            if (resp.textoLibre?.trim()) parts.push(resp.textoLibre.trim());
            updates[pregunta.campo] = parts.join('. ');
          } else {
            updates[pregunta.campo] = resp.selectedIds.join(',');
          }
        } else {
          if (!resp.selectedId) break;
          const opcion = pregunta.opciones?.find(o => o.id === resp.selectedId);
          updates[pregunta.campo] = opcion?.valor ?? resp.selectedId;
        }
        break;
      }
      case 'quick-date': {
        if (resp.opcionId === 'custom') {
          if (resp.date) updates[pregunta.campo] = resp.date;
        } else if (resp.opcionId) {
          updates[pregunta.campo] = resolveQuickDate(resp.opcionId);
        }
        break;
      }
      case 'opciones': {
        if (!resp.selectedId) break;
        const opcion = pregunta.opciones?.find(o => o.id === resp.selectedId);
        updates[pregunta.campo] = opcion?.valor ?? resp.selectedId;
        break;
      }
      case 'texto': {
        updates[pregunta.campo] = resp.text ?? '';
        break;
      }
      default:
        break;
    }
  });

  return updates;
}

// ── Reconstruir respuestas desde formData (para restaurar borradores) ─────────

export function buildRespuestasFromFormData(preguntas, formData) {
  if (!preguntas || !formData) return {};
  const respuestas = {};

  preguntas.forEach(pregunta => {
    const valor = formData[pregunta.campo];
    if (!valor) return;

    switch (pregunta.tipo) {
      case 'chips': {
        if (pregunta.multiple) {
          // Intentar encontrar opciones cuyo texto esté en el valor
          const selectedIds = (pregunta.opciones || [])
            .filter(o => !o.esTextoLibre && o.texto && valor.includes(o.texto))
            .map(o => o.id);
          if (selectedIds.length > 0) {
            respuestas[pregunta.id] = { selectedIds };
          } else if (valor) {
            // Texto libre no reconstruible como chips → guardar como textoLibre
            respuestas[pregunta.id] = { selectedIds: ['otro'], textoLibre: valor };
          }
        } else {
          // Buscar opción cuyo valor coincida
          const opcion = (pregunta.opciones || []).find(o => (o.valor ?? o.id) === valor);
          if (opcion) respuestas[pregunta.id] = { selectedId: opcion.id, valor };
        }
        break;
      }
      case 'quick-date': {
        // Buscar si coincide con una fecha resuelta
        const opcion = (pregunta.opciones || []).find(o => resolveQuickDate(o.valor) === valor);
        if (opcion) {
          respuestas[pregunta.id] = { opcionId: opcion.id };
        } else if (valor === 'Nunca') {
          respuestas[pregunta.id] = { opcionId: 'nunca' };
        } else {
          // Fecha exacta
          respuestas[pregunta.id] = { opcionId: 'custom', date: valor };
        }
        break;
      }
      case 'opciones': {
        const opcion = (pregunta.opciones || []).find(o => (o.valor ?? o.id) === valor);
        if (opcion) respuestas[pregunta.id] = { selectedId: opcion.id, valor };
        break;
      }
      case 'texto': {
        if (valor) respuestas[pregunta.id] = { text: valor };
        break;
      }
      default:
        break;
    }
  });

  return respuestas;
}
